/**
 * abilityUnlocks.ts — Persistence helpers for Nova's permanent ability flags.
 *
 * Each function reads-modify-writes the on-disk SaveData via SaveManager,
 * defaulting to a fresh save on first call. Writes are idempotent: if the
 * flag is already set, the function returns without touching storage.
 *
 * Lives under `src/game/services/` (not `app/`) so screen wrappers stay
 * thin per AGENTS.md. Errors are logged via Logger.warn, never thrown —
 * callers can `void` the promise without losing diagnostics.
 *
 * @module abilityUnlocks
 */

import { load, save, createDefaultSave } from './saveManager';
import Logger from '../utils/logger';

/**
 * Persist the double-jump unlock to save data. Called once when the player
 * clears the Void Mothership (level 2-B).
 *
 * @returns A promise that resolves when the write completes (or fails).
 */
export async function persistDoubleJumpUnlock(): Promise<void> {
  try {
    const data = (await load()) ?? createDefaultSave();
    if (data.abilities.canDoubleJump) return;
    data.abilities.canDoubleJump = true;
    data.savedAt = Date.now();
    await save(data);
    Logger.info('AbilityUnlocks', 'Double jump unlocked and persisted.');
  } catch (err) {
    Logger.warn('AbilityUnlocks', 'Failed to persist double-jump unlock', err);
  }
}

/**
 * Persist the wall-slide unlock to save data. Intended to be called once
 * when the player enters World 4 (per AGENTS.md: wall slide unlocks at the
 * start of World 4). Until World 4 ships, the `devUnlockWorld4` user
 * setting is the only path that surfaces the ability in-game.
 *
 * @returns A promise that resolves when the write completes (or fails).
 */
export async function persistWallSlideUnlock(): Promise<void> {
  try {
    const data = (await load()) ?? createDefaultSave();
    if (data.abilities.canWallSlide) return;
    data.abilities.canWallSlide = true;
    data.savedAt = Date.now();
    await save(data);
    Logger.info('AbilityUnlocks', 'Wall slide unlocked and persisted.');
  } catch (err) {
    Logger.warn('AbilityUnlocks', 'Failed to persist wall-slide unlock', err);
  }
}

/**
 * Persist the air-dash unlock to save data. Called once when the World 4
 * boss arena (`'4-B'`) is cleared — wired through the `abilityUnlock`
 * entry in `level4Session.ts`, which is what makes World 5 playable with
 * the dash per ADR-009.
 *
 * @returns A promise that resolves when the write completes (or fails).
 */
export async function persistDashUnlock(): Promise<void> {
  try {
    const data = (await load()) ?? createDefaultSave();
    if (data.abilities.canDash) return;
    data.abilities.canDash = true;
    data.savedAt = Date.now();
    await save(data);
    Logger.info('AbilityUnlocks', 'Air dash unlocked and persisted.');
  } catch (err) {
    Logger.warn('AbilityUnlocks', 'Failed to persist air-dash unlock', err);
  }
}
